// Receiving report i18n keys (used by ReceivingReports.vue).
// Page-level keys live under `ReceivingListing.*` (kept for breadcrumb compatibility).

export const reportReceivingDict: Record<string, [en: string, th: string]> = {
  // ── Page-level / breadcrumb keys (used by router) ────────
  "ReceivingListing.breadcrumbReports": ["Reports", "รายงาน"],
  "ReceivingListing.breadcrumb": ["Receiving Logs", "รายงานการรับเข้า"],

  // ── Section / page header ────────────────────────────────
  "ReceivingListing.pageTitle": ["Receiving History & Stock Check", "ประวัติการรับเข้าและตรวจสอบสต็อก"],
  "ReceivingListing.subtitle": [
    "Review past receiving transactions and verify bottle counts",
    "ดูประวัติการรับเข้าและตรวจสอบจำนวนขวดในระบบ",
  ],

  // ── Info banner ─────────────────────────────────────────
  "ReceivingListing.info.title": ["What is a Stock Check?", "ตรวจสอบสต็อกคืออะไร?"],
  "ReceivingListing.info.body": [
    "Compares the declared receiving quantity (Qty) with the actual bottle count (Bottles) for each receipt line. If Diff is not zero, the bottle count does not match and should be reviewed in the Activity Logs.",
    "เปรียบเทียบจำนวนที่ประกาศตอนรับเข้า (Qty) กับจำนวนขวดจริงในระบบ (Bottles) ของแต่ละใบรับ หาก Diff ไม่เป็น 0 แสดงว่าจำนวนขวดไม่ตรงกัน ควรตรวจสอบในบันทึกกิจกรรม",
  ],
  "ReceivingListing.info.ok": [
    "OK — bottle count matches the declared quantity",
    "จำนวนขวดในระบบตรงกับจำนวนที่รับเข้า",
  ],
  "ReceivingListing.info.mismatch": [
    "Mismatch — bottle count does not match, please review",
    "จำนวนขวดไม่ตรงกัน กรุณาตรวจสอบ",
  ],
  "ReceivingListing.info.pending": [
    "Pending — bottles have not been generated in the system yet",
    "ยังไม่ได้สร้างขวดในระบบ",
  ],

  // ── Search & filters ────────────────────────────────────
  "ReceivingListing.search": [
    "Search by receipt no, receipt ref, or supplier",
    "ค้นหาด้วยเลขที่ใบรับ เลขใบรับ หรือชื่อซัพพลายเออร์",
  ],
  "ReceivingListing.filter.issuesOnly": ["Issues only", "เฉพาะที่มีปัญหา"],
  "ReceivingListing.filter.issuesOnlyHint": [
    "Show only receipts where the bottle count does not match the declared quantity",
    "แสดงเฉพาะใบรับที่จำนวนขวดไม่ตรงกับจำนวนที่รับเข้า",
  ],
  "ReceivingListing.issueCount": ["{count} issue(s)", "{count} รายการที่มีปัญหา"],

  // ── Table column headers ────────────────────────────────
  "ReceivingListing.column.receiptRef": ["Receipt Ref", "เลขที่ใบรับ"],
  "ReceivingListing.column.wineName": ["Wine Name", "ชื่อไวน์"],
  "ReceivingListing.column.qty": ["Qty", "จำนวนที่รับเข้า"],
  "ReceivingListing.column.qtyHint": [
    "Declared receiving quantity",
    "จำนวนที่ประกาศตอนรับเข้า",
  ],
  "ReceivingListing.column.bottles": ["Bottles", "ขวดจริง"],
  "ReceivingListing.column.bottlesHint": [
    "Actual bottle count in the system",
    "จำนวนขวดที่สร้างในระบบ",
  ],
  "ReceivingListing.column.diff": ["Diff", "ส่วนต่าง"],
  "ReceivingListing.column.diffHint": ["Bottles − Qty (0 = match)", "Bottles − Qty (0 = ตรงกัน)"],
  "ReceivingListing.column.check": ["Check", "ผลตรวจ"],
  "ReceivingListing.column.receiptNo": ["Receipt No.", "เลขที่ใบรับ"],
  "ReceivingListing.column.purchaseDate": ["Purchase Date", "วันที่ซื้อ"],
  "ReceivingListing.column.purchasePrice": ["Purchase Price", "ราคาซื้อ"],
  "ReceivingListing.column.location": ["Location", "ตำแหน่ง"],
  "ReceivingListing.column.supplier": ["Supplier", "ซัพพลายเออร์"],
  "ReceivingListing.column.status": ["Status", "สถานะ"],
  "ReceivingListing.column.createdBy": ["Created By", "สร้างโดย"],
  "ReceivingListing.column.createdAt": ["Created At", "วันที่สร้าง"],
  "ReceivingListing.column.actions": ["Actions", "จัดการ"],

  // ── Cell values ─────────────────────────────────────────
  "ReceivingListing.unknownItem": ["Unknown Item", "ไม่ระบุรายการ"],
  "ReceivingListing.unassigned": ["Unassigned", "ยังไม่ระบุตำแหน่ง"],

  // ── Generation status ───────────────────────────────────
  "ReceivingListing.generation.ready": ["Ready", "ครบถ้วน"],
  "ReceivingListing.generation.issue": ["Issue", "มีปัญหา"],
  "ReceivingListing.generation.pending": ["Pending", "รอดำเนินการ"],

  // ── Receipt status ──────────────────────────────────────────
  "ReceivingListing.receiptItemStatus.active": ["Active", "ใช้งานอยู่"],
  "ReceivingListing.receiptItemStatus.voided": ["Voided", "ยกเลิกแล้ว"],

  // ── Empty state ─────────────────────────────────────────
  "ReceivingListing.empty.ok": ["No issues found — all receipts match", "ไม่พบปัญหา — จำนวนขวดทุกใบรับตรงกัน"],
  "ReceivingListing.empty": ["No receipts found", "ไม่พบใบรับ"],

  // ── Error messages ──────────────────────────────────────
  "ReceivingListing.error.title": ["Error", "เกิดข้อผิดพลาด"],
  "ReceivingListing.error.loadFailed": ["Failed to load receipts", "โหลดข้อมูลใบรับไม่สำเร็จ"],

  // ── Void receiving dialog ──────────────────────────────
  "ReceivingListing.void.title": ["Void this receiving?", "ยกเลิกการรับเข้านี้?"],
  "ReceivingListing.void.text": [
    "Receipt {receiptItem} will remain in history but will be removed from active stock.",
    "ใบรับ {receiptItem} จะยังคงอยู่ในประวัติ แต่จะถูกนำออกจากสต็อกที่ใช้งานอยู่",
  ],
  "ReceivingListing.void.confirm": ["Yes, void it", "ยืนยัน ยกเลิก"],
  "ReceivingListing.void.cancel": ["Cancel", "ยกเลิก"],
  "ReceivingListing.void.reasonLabel": ["Reason", "เหตุผล"],
  "ReceivingListing.void.reasonPlaceholder": [
    "Enter the reason for voiding this receiving",
    "ระบุเหตุผลในการยกเลิกการรับเข้า",
  ],
  "ReceivingListing.void.reasonRequired": ["Please enter a reason", "กรุณาระบุเหตุผล"],
  "ReceivingListing.void.success": ["Receiving has been voided.", "ยกเลิกการรับเข้าเรียบร้อยแล้ว"],
  "ReceivingListing.void.error": ["Failed to void receiving", "ไม่สามารถยกเลิกการรับเข้าได้"],
  "ReceivingListing.void.cannotVoid": ["Cannot void receiving", "ไม่สามารถยกเลิกการรับเข้าได้"],
  "ReceivingListing.void.blockingBottles": ["Blocking bottles:", "ขวดที่ขัดขวาง:"],
  "ReceivingListing.void.moreCount": ["...and {count} more", "...และอีก {count} รายการ"],
  "ReceivingListing.void.hint": [
    "Use the Correction workflow or scan bottles out / return them before voiding.",
    "ใช้งานผ่านขั้นตอน Correction หรือสแกนนำขวดออก/คืนก่อนทำการยกเลิก",
  ],
  "ReceivingListing.void.ok": ["OK", "ตกลง"],

  // ── Toolbar buttons / tooltips ─────────────────────────
  "ReceivingListing.refresh": ["Refresh", "รีเฟรช"],
  "ReceivingListing.viewReceiptItemActivityLogs": [
    "View activity logs for this receipt",
    "ดู Activity Logs ของใบรับนี้",
  ],
  "ReceivingListing.voidAction": ["Void receiving", "ยกเลิกการรับเข้า"],
};
